import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import Modal from "./Modal";
import Button from "./Button";
import Input from "./Input";
import { FiAlertTriangle, FiHelpCircle } from "react-icons/fi";

/**
 * Promise-based replacements for window.confirm()/window.prompt().
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title, message, confirmLabel, tone }))) return;
 *
 *   const prompt = usePrompt();
 *   const value = await prompt({ title, label, defaultValue });
 *
 * These only change *presentation*; callers keep the exact same
 * control flow (proceed only when confirmed / non-null value returned).
 */
const DialogContext = createContext(null);

export function DialogProvider({ children }) {
    const [dialog, setDialog] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const resolver = useRef(null);

    const openConfirm = useCallback((options = {}) => {
        return new Promise((resolve) => {
            resolver.current = resolve;
            setDialog({
                type: "confirm",
                title: options.title || "Konfirmasi",
                message: options.message || "Apakah Anda yakin?",
                confirmLabel: options.confirmLabel || "Ya, Lanjutkan",
                cancelLabel: options.cancelLabel || "Batal",
                tone: options.tone || "primary",
            });
        });
    }, []);

    const openPrompt = useCallback((options = {}) => {
        setInputValue(options.defaultValue ?? "");
        return new Promise((resolve) => {
            resolver.current = resolve;
            setDialog({
                type: "prompt",
                title: options.title || "Input",
                label: options.label || "",
                message: options.message || "",
                placeholder: options.placeholder || "",
                confirmLabel: options.confirmLabel || "Simpan",
                cancelLabel: options.cancelLabel || "Batal",
                tone: options.tone || "primary",
            });
        });
    }, []);

    const close = useCallback((value) => {
        if (resolver.current) {
            resolver.current(value);
            resolver.current = null;
        }
        setDialog(null);
    }, []);

    const cancel = useCallback(() => close(dialog?.type === "prompt" ? null : false), [close, dialog]);

    return (
        <DialogContext.Provider value={{ confirm: openConfirm, prompt: openPrompt }}>
            {children}

            <Modal
                open={!!dialog}
                onClose={cancel}
                title={dialog?.title}
                size="sm"
                footer={
                    <>
                        <Button variant="secondary" onClick={cancel}>
                            {dialog?.cancelLabel}
                        </Button>
                        <Button
                            variant={dialog?.tone === "danger" ? "danger" : "primary"}
                            onClick={() => close(dialog?.type === "prompt" ? inputValue : true)}
                        >
                            {dialog?.confirmLabel}
                        </Button>
                    </>
                }
            >
                {dialog?.type === "confirm" ? (
                    <div className="flex gap-3">
                        <div
                            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                                dialog.tone === "danger"
                                    ? "bg-danger-50 text-danger-600 dark:bg-danger-500/10 dark:text-danger-400"
                                    : "bg-primary-50 text-primary-600 dark:bg-primary-500/10 dark:text-primary-400"
                            }`}
                        >
                            {dialog.tone === "danger" ? <FiAlertTriangle className="h-5 w-5" /> : <FiHelpCircle className="h-5 w-5" />}
                        </div>
                        <p className="pt-1.5 text-sm text-slate-600 dark:text-slate-300">{dialog.message}</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {dialog?.message && (
                            <p className="text-sm text-slate-600 dark:text-slate-300">{dialog.message}</p>
                        )}
                        <Input
                            label={dialog?.label}
                            placeholder={dialog?.placeholder}
                            value={inputValue}
                            autoFocus
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") close(inputValue);
                            }}
                        />
                    </div>
                )}
            </Modal>
        </DialogContext.Provider>
    );
}

export function useConfirm() {
    const ctx = useContext(DialogContext);
    if (!ctx) throw new Error("useConfirm must be used within DialogProvider");
    return ctx.confirm;
}

export function usePrompt() {
    const ctx = useContext(DialogContext);
    if (!ctx) throw new Error("usePrompt must be used within DialogProvider");
    return ctx.prompt;
}
